/**
 * Helper functions for PricingProvider error handling and utilities
 */
import { logError } from "@/lib/logError";
import type {
  CreatePricingEngineRequest,
  PricingSwapInstruction,
} from "@generated/redux/services/generated/api";

/**
 * Extracts error message and type from API error response
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractErrorDetails = (err: any) => {
  const errorMessage = err?.data?.messages?.[0] || err?.messages?.[0];
  const errorType = err?.data?.type || err?.type;
  return { errorMessage, errorType };
};

/**
 * Processes error messages and returns user-friendly error text
 */
export const processErrorMessage = (
  errorMessage: string | undefined,
  errorType: string | undefined,
  tCustomError: (key: string, params?: Record<string, string>) => string,
  sellCurrency: string,
  buyCurrency: string
): string => {
  if (!errorMessage) {
    return tCustomError("UNKNOWN");
  }

  // Check if error type is internal service exception
  if (
    errorType === "internal-service-exception" ||
    errorType === "quote-delivery-amount-not-positive" ||
    errorType === "constraint-violation-exception"
  ) {
    return tCustomError("UNKNOWN");
  }

  if (errorType === "quote-remittance-amount-not-positive") {
    return tCustomError("CHECK_INPUTS");
  }

  // Check if error type is market price exception
  if (errorType === "market-price-exception") {
    return tCustomError("NO_MARKET_PRICE", {
      sellCurrency,
      buyCurrency,
    });
  }

  return errorMessage;
};

/**
 * Handles API errors by extracting, processing, and setting error state
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const handleApiError = (
  err: any,
  /* eslint-enable @typescript-eslint/no-explicit-any */
  defaultMessage: string,
  setCustomError: (error: string) => void,
  tCustomError: (key: string, params?: Record<string, string>) => string,
  sellCurrency: string,
  buyCurrency: string
) => {
  // The PricingProvider catch blocks wrap response-handling code, not just
  // the RTK call — a frontend TypeError lands here too and would otherwise
  // die as a user-facing "UNKNOWN" error with zero Sentry signal. logError
  // is double-capture-safe: RTK errors (already captured by baseQuery) are
  // skipped; genuine Error instances become Sentry events.
  logError(defaultMessage, err);
  const { errorMessage, errorType } = extractErrorDetails(err);
  if (errorMessage) {
    setCustomError(
      processErrorMessage(
        errorMessage,
        errorType,
        tCustomError,
        sellCurrency,
        buyCurrency
      )
    );
  } else {
    setCustomError(defaultMessage);
  }
};

/**
 * How the two currencies of a pricing instruction relate.
 *
 * `Unresolved` is its own answer: an instruction that is missing, or missing
 * either currency, says nothing about the pair. Comparing the raw fields would
 * call it "same" (`undefined === undefined`, and the draft's `"" === ""`),
 * which collapses the sell row and drops the exchange rate on a payment that
 * converts.
 */
export enum CurrencyRelation {
  Same = "SAME",
  Different = "DIFFERENT",
  Unresolved = "UNRESOLVED",
}

export function getCurrencyRelation(
  instruction:
    | { payCurrency?: string | null; receiveCurrency?: string | null }
    | null
    | undefined
): CurrencyRelation {
  const payCurrency = instruction?.payCurrency;
  const receiveCurrency = instruction?.receiveCurrency;
  if (!payCurrency || !receiveCurrency) return CurrencyRelation.Unresolved;
  return payCurrency === receiveCurrency
    ? CurrencyRelation.Same
    : CurrencyRelation.Different;
}

/**
 * The provider's `isSameCurrency` flag, read off `getCurrencyRelation`.
 *
 * A resolved pair answers directly. An unresolved one keeps the reading its
 * consumers were written against: neither currency yet (no instruction at
 * all, or one with both fields empty) reads as same, while an instruction
 * carrying only one currency reads as different. An empty string and a
 * missing field are both "no currency", so a mixed pair (`""` with
 * `undefined`, either way round) reads as same too, where raw equality would
 * call it different. Screens that must tell "unresolved" apart from "same"
 * read `currencyRelation` instead.
 */
export function getIsSameCurrency(
  instruction: Parameters<typeof getCurrencyRelation>[0]
): boolean {
  const relation = getCurrencyRelation(instruction);
  if (relation !== CurrencyRelation.Unresolved) {
    return relation === CurrencyRelation.Same;
  }
  return !instruction?.payCurrency && !instruction?.receiveCurrency;
}

/**
 * The body of a pricing-request create.
 *
 * It carries no `originator`: the BFF derives the originator from the
 * signed-in user's token and never reads the body's deprecated field.
 */
export function buildCreatePricingEngineRequest({
  customerAccountId,
  instruction,
}: {
  customerAccountId: string;
  instruction: PricingSwapInstruction;
}): CreatePricingEngineRequest {
  return {
    customerAccount: customerAccountId,
    instructions: [instruction],
  };
}
